import { useAzureUrl } from "../../../context/Dashboard/azure/contextUrl.js";
import { useDesign } from "../../../context/contextDesign.js";

const ScanCard = () => {
  const {
    urlScanLoading,
    urlDashboardView,
    handleSubmit,
    setUrl,
    url,
    error
  } = useAzureUrl();
  const { toggleUpgradeCard } = useDesign();
  const urlExamples = [
    { label: "Blob Container", value: "https://mystorage.blob.core.windows.net/mycontainer" },
    { label: "Specific Blob", value: "https://mystorage.blob.core.windows.net/mycontainer/data.zip" },
    { label: "Static Website", value: "https://mystorage.z13.web.core.windows.net/" }
  ];

  return (
    <div className="scan-card d-flex justify-content-center align-items-center">
      <div className="card shadow-sm border-1 rounded-4">
        <div className="d-flex flex-row align-items-center h-100">

          {/* 🔹 Left: Form Section */}
          <div className="scan-form-section p-3 w-50">
            <form
              onSubmit={handleSubmit}
              className="d-flex flex-column justify-content-center align-items-center"
            >
              <div className="fs-1 fw-bold text-center heading mb-3">
                Scan Blob URL
              </div>

              <div
                className="fs-6 text-center fw-semibold mb-5 subheading text-muted"
                style={{ width: 280 }}>
                Enter the blob url below to scan for potential vulnerabilities.
              </div>

              <div
                className="mb-2 w-100 input-group rounded-2"
                style={{ height: 43 }}
              >
                <input
                  type="text"
                  placeholder="Enter S3 URL"
                  className="p-2 text-center form-control"
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={urlScanLoading || urlDashboardView}
                />
              </div>

              <div style={{ width: 260 }}>
                {error && (
                  <div
                    className="error-hover text-danger text-center fw-semibold mb-2"
                    style={{ cursor: 'pointer', fontSize: '14px' }}
                    onClick={error.card ? toggleUpgradeCard : null}>
                    {error.message}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-success w-100 py-2 fw-semibold fs-6"
                disabled={urlScanLoading || urlDashboardView || !url}
                style={{ height: 46 }}
              >
                Submit
              </button>
            </form>
          </div>

          <div className="scan-video-section h-100 w-50 p-4 bg-light d-flex flex-column border-start overflow-auto">
            {/* Documentation Header */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-book-half text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">STORAGE DOCUMENTATION</span>
              </div>
              <p className="text-muted" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                Azure Blob Storage URLs provide access to your unstructured data.
                To scan correctly, ensure your container or blob URL follows the Azure standard naming conventions.
              </p>
            </div>

            {/* Header */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-shield-lock-fill text-primary fs-5"></i>
                <span className="fw-bold text-dark tracking-tight">AZURE DEPLOYMENT GUIDE</span>
              </div>
              <p className="text-muted" style={{ fontSize: '12.5px', lineHeight: '1.5' }}>
                Follow these steps to create your storage container and retrieve a compatible URL for security scanning.
              </p>
            </div>

            {/* Full Process Steps */}
            <div className="d-flex flex-column gap-4 mb-4">

              {/* Step 1: Creation */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 1</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Storage Creation</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Log in to <strong>Azure Portal</strong>, search for <strong>Storage accounts</strong>, and click <strong>Create</strong>.</>,
                    <>Once created, navigate to <strong>Containers</strong> under the Data Storage menu and add a new container.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 2: Access & Policy */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 2</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Set Permissions</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>In the container's <strong>Settings</strong>, change the <strong>Public access level</strong> to 'Blob' or 'Container'.</>,
                    <>Configure <strong>Shared Access Signatures (SAS)</strong> if temporary restricted access is required.</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Step 3: URL Copy */}
              <div className="step-item">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>STEP 3</span>
                  <span className="fw-bold text-dark" style={{ fontSize: '13px' }}>Retrieve Endpoint</span>
                </div>

                <ul className="list-unstyled d-flex flex-column gap-2">
                  {[
                    <>Select your container or a specific blob and go to the <strong>Properties</strong> tab.</>,
                    <>Copy the <strong>URL</strong> field (e.g., https://account.blob.core.windows.net/container).</>
                  ].map((step, i) => (
                    <li key={i} className="d-flex gap-2 align-items-start" style={{ fontSize: '12px' }}>
                      <span className="badge rounded-circle bg-primary-subtle text-primary p-1" style={{ width: '18px', height: '18px', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {i + 1}
                      </span>
                      <span className="text-dark-emphasis">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Supported Examples Footer */}
            <div className="mt-auto pt-3 border-top">
              <div className="mb-2 d-flex align-items-center gap-2">
                <i className="bi bi-info-circle-fill text-success"></i>
                <span className="fw-bold small text-secondary">VALID AZURE FORMATS</span>
              </div>

              <div className="d-flex flex-column gap-2">
                {urlExamples.map((ex, i) => (
                  <div key={i} className="p-2 rounded bg-white border border-light-subtle shadow-sm">
                    <div className="text-primary fw-bold mb-1" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{ex.label}</div>
                    <code className="text-dark-emphasis d-block text-truncate p-1 bg-light rounded" style={{ fontSize: '11px' }}>
                      {ex.value}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanCard;
